/**
 * NIP-49: private key encryption (ncryptsec).
 * Payload = 0x02 || strength || salt(16) || nonce(24) || XChaCha20(scrypt(pw, salt), nonce, sk)
 * bech32-encoded with the "ncryptsec" prefix. scrypt N=2^16, r=8, p=1 per spec.
 */
import { xchacha20 } from '@noble/ciphers/chacha.js';
import { scrypt } from '@noble/hashes/scrypt.js';
import { randomBytes, hexToBytes, bytesToHex } from '@noble/hashes/utils.js';
import { bech32 } from '@scure/base';

const SCRYPT = { N: 2 ** 16, r: 8, p: 1, dkLen: 32 };
const VERSION = 0x02;

function passwordStrength(pw: string): number {
  return pw.length >= 12 ? 0x02 : pw.length >= 8 ? 0x01 : 0x00;
}

export const isEncryptedKey = (s: string) => s.startsWith('ncryptsec1');

export function encryptKey(skHex: string, password: string): string {
  if (!/^[0-9a-f]{64}$/.test(skHex)) throw new Error('invalid secret key');
  if (!password) throw new Error('passphrase required');
  const salt = randomBytes(16);
  const nonce = randomBytes(24);
  const key = scrypt(password.normalize('NFKC'), salt, SCRYPT);
  const cipher = xchacha20(key, nonce, hexToBytes(skHex));
  const payload = new Uint8Array([VERSION, passwordStrength(password), ...salt, ...nonce, ...cipher]);
  return bech32.encode('ncryptsec', bech32.toWords(payload), false as unknown as number);
}

export function decryptKey(ncryptsec: string, password: string): string {
  const { prefix, words } = bech32.decode(ncryptsec as `${string}1${string}`, false as unknown as number);
  if (prefix !== 'ncryptsec') throw new Error('not an ncryptsec key');
  const payload = new Uint8Array(bech32.fromWords(words));
  if (payload.length !== 74 || payload[0] !== VERSION) throw new Error('unsupported ncryptsec payload');
  const salt = payload.slice(2, 18);
  const nonce = payload.slice(18, 42);
  const cipher = payload.slice(42, 74);
  const key = scrypt(password.normalize('NFKC'), salt, SCRYPT);
  const sk = xchacha20(key, nonce, cipher); // stream cipher: decrypt = encrypt
  return bytesToHex(sk);
}
